import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import useReveal from "@/hooks/useReveal";
import api from "@/lib/api";
import PersonalizedServicesSection from "@/components/PersonalizedServicesSection";
import {
  ArrowUpRight,
  Calculator,
  Receipt,
  Users,
  Sparkles,
  CheckCircle2,
  TrendingUp,
  Clock,
  MessagesSquare,
  ShieldCheck,
  FileText,
  Mail,
} from "lucide-react";

function formatDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return "";
  }
}

const HERO_IMG =
  "https://images.unsplash.com/photo-1575516478880-7dfb1a114073?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBmaW5hbmNlJTIwYnVpbGRpbmd8ZW58MHx8fHwxNzgzNTk2Mjk2fDA&ixlib=rb-4.1.0&q=85";
const ABOUT_IMG = "https://images.pexels.com/photos/12903168/pexels-photo-12903168.jpeg";
const TRANSMISSION_IMG =
  "https://images.unsplash.com/photo-1439434768192-c60615c1b3c8?crop=entropy&cs=srgb&fm=jpg&w=1400&q=80";

const services = [
  { icon: Calculator, title: "Comptabilité & Bilan", desc: "Tenue et révision comptable, établissement des comptes annuels en toute conformité.", slug: "comptabilite" },
  { icon: Receipt, title: "Fiscalité & Impôts", desc: "Optimisation fiscale, liasses fiscales et accompagnement lors des contrôles.", slug: "fiscalite" },
  { icon: Users, title: "Paie & Ressources Humaines", desc: "Bulletins de paie, déclarations sociales et conseil en droit du travail.", slug: "paie" },
  { icon: Sparkles, title: "Conseil & Création d'entreprise", desc: "Business plan, choix du statut juridique et accompagnement au démarrage.", slug: "creation" },
];

const differentiators = [
  { icon: MessagesSquare, title: "Un interlocuteur dédié", text: "Un contact unique et disponible à chaque étape de votre activité." },
  { icon: TrendingUp, title: "Outils digitaux", text: "Suivez vos comptes en temps réel via votre espace client." },
  { icon: ShieldCheck, title: "Équipe pluridisciplinaire", text: "Expertise comptable, fiscalité, social — un seul point d'entrée." },
  { icon: CheckCircle2, title: "Honoraires transparents", text: "Devis clair et forfaits adaptés, sans surprise en fin d'année." },
];

const steps = [
  { n: "01", title: "Diagnostic & valorisation", text: "Analyse patrimoniale, fiscale et valorisation de l'entreprise à céder." },
  { n: "02", title: "Structuration de l'opération", text: "Choix du schéma juridique et fiscal (apport-cession, art. 150-0 B ter, holding)." },
  { n: "03", title: "Coordination interprofessionnelle", text: "Pilotage conjoint avec notaire, avocat fiscaliste et conseiller en gestion de patrimoine." },
  { n: "04", title: "Cession & remploi", text: "Négociation, sécurisation de l'opération et stratégie de remploi des fonds." },
];

const HomePage = () => {
  const ref = useReveal();
  const [preview, setPreview] = useState(null);
  useEffect(() => {
    api.get("/newsletter/public?limit=3").then((r) => setPreview(r.data)).catch(() => setPreview([]));
  }, []);
  return (
    <div ref={ref} data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden bg-teal-dark text-white grain">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="" className="w-full h-full object-cover opacity-55" />
          <div className="absolute inset-0 bg-teal-dark/55" />
        </div>
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 pt-24 pb-32 md:pt-32 md:pb-40">
          <div className="grid lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-8 reveal">
              <p className="eyebrow text-teal-l3">Depuis 1968 · Provence Alpes Côte d'Azur</p>
              <h1 className="mt-6 font-serif text-5xl md:text-7xl lg:text-[88px] leading-[0.98] font-light">
                Votre expert-comptable,<br />
                <em className="text-teal-l3">partenaire</em> de votre réussite.
              </h1>
              <p className="mt-8 max-w-2xl text-lg md:text-xl text-teal-l3 leading-relaxed">
                Comptabilité, fiscalité, paie et conseil : un accompagnement sur-mesure pour
                entrepreneurs, TPE et PME.
              </p>
              <div className="mt-10 flex flex-wrap gap-4">
                <Link
                  to="/contact"
                  data-testid="hero-cta-devis"
                  className="bg-white text-teal-dark px-7 py-4 inline-flex items-center gap-2 hover:bg-teal-l1 transition-colors"
                >
                  Demander un devis gratuit <ArrowUpRight size={18} />
                </Link>
                <a
                  href="#services"
                  data-testid="hero-cta-services"
                  className="border border-white/60 text-white px-7 py-4 inline-flex items-center gap-2 hover:bg-white hover:text-teal-dark transition-colors"
                >
                  Nos services
                </a>
              </div>
            </div>

            <div className="lg:col-span-4 reveal">
              <div className="border border-white/25 p-6 backdrop-blur-sm bg-white/5" data-testid="hero-newsletter-preview">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="eyebrow text-teal-l3">Newsletter du cabinet</p>
                    <p className="font-serif text-2xl mt-2 leading-tight">Dernières publications</p>
                  </div>
                  <Mail size={22} className="text-teal-l3 shrink-0" />
                </div>

                <ul className="mt-6 space-y-4" data-testid="hero-newsletter-items">
                  {preview === null && (
                    <li className="text-sm text-teal-l3">Chargement…</li>
                  )}
                  {preview !== null && preview.length === 0 && (
                    <li className="text-sm text-teal-l3">
                      Nos premières publications arriveront très bientôt. Créez un compte pour les recevoir en priorité.
                    </li>
                  )}
                  {preview && preview.map((doc) => (
                    <li key={doc.id} className="border-b border-white/15 pb-3 last:border-0">
                      <span className="eyebrow text-xs text-teal-l3">{doc.category || "Actualité"}</span>
                      <p className="font-serif text-lg leading-snug mt-1">{doc.title}</p>
                      <p className="text-xs text-teal-l3 mt-1">Publié le {formatDate(doc.created_at)}</p>
                    </li>
                  ))}
                </ul>

                <div className="mt-6 flex flex-wrap gap-4 items-center">
                  <Link
                    to="/inscription"
                    data-testid="hero-newsletter-register"
                    className="inline-flex items-center gap-2 text-sm bg-white text-teal-dark px-4 py-2 hover:bg-teal-l1 transition-colors"
                  >
                    Créer un compte <ArrowUpRight size={14} />
                  </Link>
                  <Link
                    to="/login"
                    data-testid="hero-dashboard-link"
                    className="text-sm border-b border-white/60 pb-0.5 hover:text-teal-l3 inline-flex items-center gap-1"
                  >
                    J'ai déjà un compte
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Marquee */}
        <div className="relative bg-teal-mid border-t border-white/10 overflow-hidden">
          <div className="flex whitespace-nowrap marquee-track py-4">
            {Array.from({ length: 2 }).map((_, i) => (
              <div key={i} className="flex items-center gap-12 pr-12 eyebrow text-teal-l3">
                <span>Inscrit à l'Ordre des Experts-Comptables de Provence Alpes Côte d'Azur</span><span>·</span>
                <span>Depuis 1968</span><span>·</span>
                <span>500+ clients accompagnés</span><span>·</span>
                <span>Cabinet à taille humaine</span><span>·</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CABINET / ABOUT */}
      <section id="cabinet" className="py-24 md:py-32 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-14 items-start">
          <div className="lg:col-span-5 reveal">
            <p className="eyebrow text-teal-accent">Le Cabinet</p>
            <h2 className="mt-4 font-serif text-4xl md:text-5xl leading-[1.05] text-teal-dark">
              Un cabinet à taille humaine, une expertise reconnue.
            </h2>
            <p className="mt-6 text-ink-500 leading-relaxed">
              Depuis plus de cinquante ans, Ducrot &amp; Associés accompagne chefs d'entreprise,
              artisans et professions libérales à travers un service personnalisé, transparent
              et exigeant. Notre équipe pluridisciplinaire couvre l'ensemble de vos besoins
              comptables, fiscaux et sociaux.
            </p>
            <div className="mt-10 grid grid-cols-2 gap-8">
              <div className="border-t border-teal-dark pt-4">
                <div className="font-serif text-5xl text-teal-dark num-hover">1968</div>
                <p className="eyebrow text-ink-500 mt-2">Depuis</p>
              </div>
              <div className="border-t border-teal-dark pt-4">
                <div className="font-serif text-5xl text-teal-dark num-hover">500+</div>
                <p className="eyebrow text-ink-500 mt-2">Clients accompagnés</p>
              </div>
              <div className="border-t border-teal-dark pt-4">
                <div className="font-serif text-5xl text-teal-dark num-hover">1:1</div>
                <p className="eyebrow text-ink-500 mt-2">Un interlocuteur par client</p>
              </div>
              <div className="border-t border-teal-dark pt-4">
                <div className="font-serif text-5xl text-teal-dark num-hover">&lt; 24h</div>
                <p className="eyebrow text-ink-500 mt-2">Délai de réponse moyen</p>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7 reveal">
            <div className="grid grid-cols-6 gap-4">
              <div className="col-span-4 row-span-2 overflow-hidden">
                <img src={ABOUT_IMG} alt="Équipe Ducrot" className="w-full h-[520px] object-cover" />
              </div>
              <div className="col-span-2 bg-teal-l1 p-6 flex flex-col justify-between">
                <Clock className="text-teal-dark" size={28} />
                <div>
                  <p className="font-serif text-3xl text-teal-dark leading-tight">Réactivité</p>
                  <p className="text-xs text-ink-500 mt-2">Réponse sous 24h ouvrées</p>
                </div>
              </div>
              <div className="col-span-2 bg-teal-dark text-white p-6 flex flex-col justify-between">
                <ShieldCheck size={28} />
                <div>
                  <p className="font-serif text-3xl leading-tight">Confiance</p>
                  <p className="text-xs text-teal-l3 mt-2">Honoraires transparents</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section id="services" className="py-24 md:py-32 bg-teal-l1">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="grid lg:grid-cols-12 gap-8 items-end reveal">
            <div className="lg:col-span-7">
              <p className="eyebrow text-teal-accent">Nos services</p>
              <h2 className="mt-4 font-serif text-4xl md:text-5xl leading-[1.05] text-teal-dark">
                Un accompagnement complet pour votre entreprise.
              </h2>
            </div>
            <p className="lg:col-span-5 text-ink-500 leading-relaxed">
              De la tenue comptable à la création d'entreprise, nous couvrons tous les besoins
              de votre activité, avec la rigueur et la disponibilité d'un partenaire de long terme.
            </p>
          </div>

          <div className="mt-14 grid md:grid-cols-2 gap-px bg-teal-dark/15 border border-teal-dark/15">
            {services.map(({ icon: Icon, title, desc, slug }) => (
              <Link
                key={slug}
                to={`/services#${slug}`}
                data-testid={`service-card-${slug}`}
                className="reveal group bg-white p-8 md:p-10 flex flex-col justify-between min-h-[280px] hover:bg-teal-dark hover:text-white transition-colors"
              >
                <div>
                  <Icon size={32} className="text-teal-accent group-hover:text-white transition-colors" />
                  <h3 className="mt-6 font-serif text-3xl md:text-4xl leading-tight">{title}</h3>
                  <p className="mt-4 text-sm text-ink-500 group-hover:text-teal-l3 transition-colors leading-relaxed">{desc}</p>
                </div>
                <div className="mt-8 inline-flex items-center gap-2 text-sm">
                  En savoir plus <ArrowUpRight size={16} className="group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* POURQUOI NOUS CHOISIR */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="max-w-3xl reveal">
            <p className="eyebrow text-teal-accent">Pourquoi nous choisir</p>
            <h2 className="mt-4 font-serif text-4xl md:text-5xl text-teal-dark leading-[1.05]">
              Une méthode éprouvée, une relation durable.
            </h2>
          </div>

          <div className="mt-16 grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {differentiators.map(({ icon: Icon, title, text }) => (
              <div key={title} className="reveal border border-ink-200 p-8 hover:border-teal-dark transition-colors">
                <Icon size={26} className="text-teal-accent" />
                <h3 className="mt-6 font-serif text-2xl text-teal-dark leading-tight">{title}</h3>
                <p className="mt-3 text-sm text-ink-500 leading-relaxed">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TRANSMISSION */}
      <section id="transmission" className="py-24 md:py-32 bg-teal-dark text-white relative overflow-hidden grain">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-14">
          <div className="lg:col-span-5 reveal">
            <p className="eyebrow text-teal-l3">Notre spécialité</p>
            <h2 className="mt-4 font-serif text-4xl md:text-5xl leading-[1.05]">
              Transmission &amp; Apport-Cession d'entreprise.
            </h2>
            <p className="mt-6 text-teal-l3 leading-relaxed">
              Un accompagnement sur-mesure porté par une équipe interprofessionnelle, pour
              sécuriser chaque étape de votre opération.
            </p>

            <div className="mt-10 flex flex-wrap gap-2">
              {["Expert-comptable", "Notaire", "Avocat fiscaliste", "Conseil en gestion de patrimoine"].map((r) => (
                <span key={r} className="text-xs eyebrow border border-white/30 px-3 py-2">
                  {r}
                </span>
              ))}
            </div>

            <div className="mt-10">
              <img src={TRANSMISSION_IMG} alt="" className="w-full h-72 object-cover" />
            </div>
          </div>

          <div className="lg:col-span-7">
            <ol className="space-y-px">
              {steps.map((s) => (
                <li key={s.n} className="reveal grid grid-cols-12 gap-6 border-t border-white/15 py-8 group">
                  <div className="col-span-2 font-serif text-4xl md:text-5xl text-teal-l3 group-hover:text-white transition-colors">{s.n}</div>
                  <div className="col-span-10">
                    <h3 className="font-serif text-2xl md:text-3xl leading-tight">{s.title}</h3>
                    <p className="mt-3 text-teal-l3 text-sm md:text-base leading-relaxed max-w-xl">{s.text}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* SERVICES PERSONNALISÉS */}
      <PersonalizedServicesSection />

      {/* HONORAIRES */}
      <section id="honoraires" className="py-24 md:py-32 bg-teal-l1">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 reveal">
            <p className="eyebrow text-teal-accent">Honoraires</p>
            <h2 className="mt-4 font-serif text-4xl md:text-5xl leading-[1.05] text-teal-dark">
              Des honoraires transparents, sans surprise.
            </h2>
            <p className="mt-6 text-ink-500 leading-relaxed max-w-2xl">
              Chaque mission fait l'objet d'un devis clair et détaillé, adapté à la taille de
              votre structure et à la complexité de votre activité. Nous privilégions les
              forfaits annuels pour vous offrir une visibilité totale sur votre budget.
            </p>
          </div>
          <div className="lg:col-span-5 reveal">
            <Link to="/contact" data-testid="honoraires-cta" className="btn-primary w-full justify-center text-base">
              Demander un devis personnalisé <ArrowUpRight size={18} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
